import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} GrooviDiscountBadgeRefs
 * @property {HTMLElement} badgeContent
 * @property {HTMLElement} productBadges
 * @property {HTMLElement} variantBadges
 */

/**
 * Badge row above the product title. Owns two containers:
 * - [ref="productBadges"] — static list from custom.product_badges metaobject
 * - [ref="variantBadges"] — discount pill for the selected variant, rebuilt
 *   from window.grooviBadgeData on variant change
 *
 * Substitution rule:
 *   If the selected variant has a discount → show variant container, hide product container.
 *   Else if the product has product_badges → show product container, hide variant container.
 *   Else → collapse the wrapper entirely.
 *
 * Hidden↔visible state is toggled instantly via the
 * `.groovi-discount-badge--hidden` class (CSS controls visibility only — no transition).
 *
 * @extends {Component<GrooviDiscountBadgeRefs>}
 */
class GrooviDiscountBadge extends Component {
  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section');
    if (!section) return;
    section.addEventListener(StandardEvents.productSelect, this.updateBadge);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section');
    if (section) {
      section.removeEventListener(StandardEvents.productSelect, this.updateBadge);
    }
  }

  updateBadge = (event) => {
    // Horizon 4.x: ignore product-card variant changes elsewhere on the page.
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    // 4.x ProductSelectEvent resolves a deferred promise with the new variant
    // (upgrade playbook §5.3). detail.resource = variant JSON, detail.productId = product id.
    event.promise?.then(({ detail }) => this.#applyBadge(detail)).catch(() => {});
  };

  #applyBadge(detail) {
    const variantId = detail?.resource?.id;
    const productId = detail?.productId;
    if (!variantId || !productId) return;

    // Combined listings can mount more than one badge block on a page;
    // only react to updates for our own product.
    if (String(productId) !== String(this.dataset.productId)) return;

    const variantData = window.grooviBadgeData?.[productId]?.[variantId];
    if (!variantData) return;

    const { productBadges, variantBadges } = this.refs;
    if (!productBadges || !variantBadges) return;

    const hasDiscount = Boolean(variantData.discount);
    const hasProductBadges = this.dataset.hasProductBadges === 'true';

    let targetMode = 'empty';
    if (hasDiscount) targetMode = 'variant';
    else if (hasProductBadges) targetMode = 'product';

    // Rebuild the variant container from data — cheap, keeps the
    // markup authoritative relative to window.grooviBadgeData.
    this.#populateVariant(variantBadges, variantData);

    const applyContainerVisibility = () => {
      productBadges.hidden = targetMode !== 'product';
      variantBadges.hidden = targetMode !== 'variant';
    };

    const willBeHidden = targetMode === 'empty';
    const isCurrentlyHidden = this.classList.contains('groovi-discount-badge--hidden');

    if (willBeHidden === isCurrentlyHidden) {
      applyContainerVisibility();
      return;
    }

    if (willBeHidden) {
      this.classList.add('groovi-discount-badge--hidden');
      applyContainerVisibility();
      return;
    }

    applyContainerVisibility();
    this.classList.remove('groovi-discount-badge--hidden');
  };

  #populateVariant(container, data) {
    container.replaceChildren();
    if (data.discount) {
      container.append(this.#createPill(data.discount));
    }
  }

  #createPill(label) {
    const span = document.createElement('span');
    span.className = 'groovi-pill groovi-font--easypixel';
    span.textContent = label;
    return span;
  }
}

if (!customElements.get('groovi-discount-badge')) {
  customElements.define('groovi-discount-badge', GrooviDiscountBadge);
}
